import { useEffect, useCallback, useRef } from 'react';

interface UseLeftEdgeSwipeDisableOptions {
  /**
   * Whether to enable the left edge swipe prevention
   * @default true
   */
  enabled?: boolean;
  
  /**
   * The edge width in pixels to detect left edge swipes
   * @default 50
   */
  edgeWidth?: number;
  
  /**
   * Custom page identifier for logging purposes
   * @default 'Page'
   */
  pageId?: string;
  
  /**
   * Whether to log blocked swipes to console
   * @default true
   */
  enableLogging?: boolean;
}

/**
 * Custom hook to disable left edge swipe gestures on mobile devices
 * Prevents accidental browser back navigation that could cause logout
 * Preserves vertical scrolling and only blocks horizontal swipes
 * 
 * @param options Configuration options for the hook
 * 
 * @example
 * ```tsx
 * // Basic usage - applies to all mobile devices
 * useLeftEdgeSwipeDisable();
 * 
 * // With custom options
 * useLeftEdgeSwipeDisable({
 *   pageId: 'Dashboard',
 *   edgeWidth: 60,
 *   enableLogging: false
 * });
 * 
 * // Conditionally disable (e.g., only on main pages, not sub-pages)
 * useLeftEdgeSwipeDisable({
 *   enabled: !isSubPage,
 *   pageId: 'Profile'
 * });
 * ```
 */
export const useLeftEdgeSwipeDisable = (options: UseLeftEdgeSwipeDisableOptions = {}) => {
  const {
    enabled = true,
    edgeWidth = 50,
    pageId = 'Page',
    enableLogging = true
  } = options;

  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isScrollingRef = useRef(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled) return;
    
    const touch = e.touches[0];
    const isLeftEdge = touch.clientX <= edgeWidth;
    
    if (isLeftEdge) {
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now()
      };
      
      if (enableLogging) {
        console.log(`🔍 [${pageId}] Left edge touch detected at ${touch.clientX}px - monitoring gesture`);
      }
      
      // DON'T prevent immediately - let the gesture develop first
      // This allows vertical scrolling to work normally
    }
  }, [enabled, edgeWidth, pageId, enableLogging]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !touchStartRef.current) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // Determine gesture intention
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // Only prevent if this is clearly a horizontal swipe
    const isHorizontalGesture = absDeltaX > absDeltaY && absDeltaX > 10;
    const isRightwardSwipe = deltaX > 0;
    
    if (isHorizontalGesture && isRightwardSwipe) {
      // This looks like a horizontal swipe from left edge - prevent it
      if (enableLogging) {
        console.log(`🚫 [${pageId}] Preventing horizontal swipe: deltaX=${deltaX}, deltaY=${deltaY}`);
      }
      
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    } else if (absDeltaY > absDeltaX && absDeltaY > 10) {
      // This is clearly vertical scrolling - allow it
      isScrollingRef.current = true;
      if (enableLogging) {
        console.log(`✅ [${pageId}] Allowing vertical scroll: deltaX=${deltaX}, deltaY=${deltaY}`);
      }
    }
  }, [enabled, pageId, enableLogging]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled || !touchStartRef.current) return;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;
    
    // Check if this was a completed navigation gesture
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const isHorizontalGesture = absDeltaX > absDeltaY;
    const isRightwardSwipe = deltaX > 30; // Minimum swipe distance
    const isFastGesture = deltaTime < 800; // Not too slow
    
    if (isHorizontalGesture && isRightwardSwipe && isFastGesture && !isScrollingRef.current) {
      // This was a navigation gesture - prevent it
      if (enableLogging) {
        console.log(`🚫 [${pageId}] Prevented navigation gesture: deltaX=${deltaX}, deltaY=${deltaY}, time=${deltaTime}ms`);
      }
      
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
    
    // Reset tracking
    touchStartRef.current = null;
    isScrollingRef.current = false;
  }, [enabled, pageId, enableLogging]);

  useEffect(() => {
    if (!enabled) return;

    // Only work on mobile devices
    const isMobile = window.innerWidth < 768 || /Mobile|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
    if (!isMobile) return;

    if (enableLogging) {
      console.log(`🛡️ [${pageId}] Setting up smart left edge swipe prevention (preserves vertical scrolling)`);
    }

    // Set CSS to prevent overscroll behavior for horizontal only
    const originalBodyStyle = document.body.style.overscrollBehaviorX;
    const originalDocumentStyle = document.documentElement.style.overscrollBehaviorX;
    
    document.body.style.overscrollBehaviorX = 'none';
    document.documentElement.style.overscrollBehaviorX = 'none';

    // Add event listeners with passive: false only for touchmove to allow preventDefault
    document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true, capture: true });

    return () => {
      if (enableLogging) {
        console.log(`🛡️ [${pageId}] Cleaning up smart left edge swipe prevention`);
      }
      
      // Restore CSS
      document.body.style.overscrollBehaviorX = originalBodyStyle;
      document.documentElement.style.overscrollBehaviorX = originalDocumentStyle;
      
      // Remove event listeners
      document.removeEventListener('touchstart', handleTouchStart, true);
      document.removeEventListener('touchmove', handleTouchMove, true);
      document.removeEventListener('touchend', handleTouchEnd, true);
    };
  }, []); // Empty dependency array - only register once

  return {
    isLeftEdgeSwipeDisabled: enabled,
    edgeWidth
  };
}; 